import { produce } from 'immer';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useLocation, useParams } from 'react-router-dom';
import { HttpResponse } from '../../../../../../../common/ajax';
import {
  getErrorMessage,
  hasValidationErrors,
  setValidationErrors,
  showErrorToast,
  updateQueryStringParameter,
} from '../../../../../../../common/global/helpers';
import useMount from '../../../../../../../common/hooks/useMount';
import Breadcrumbs from '../../../../../../../common/widgets/Breadcrumbs';
import Loader from '../../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../../common/widgets/LoadingSpinner';
import {
  Notification,
  NotificationType,
} from '../../../../../../../common/widgets/Notification';
import { CustomerBudgetContext } from '../../../../../contexts/CustomerBudgetContext';
import { GiftCardBeneficiaryType } from '../../../../../enums';
import {
  CardProductModel,
  ComputedCardProductPriceModel,
  TopUpDetailsModel,
} from '../../../../../models/Cards';
import { Dictionary } from '../../../../../models/Categories';
import { CheckoutResponseModel } from '../../../../../models/Checkout';
import { ErrorState, ErrorType } from '../../../../../models/Errors';
import { resources } from '../../../../../resources/resources';
import { cardService } from '../../../../../services/card';
import applicationCrumbs from '../../../../ApplicationCrumbs';
import { LimitErrorDetailsDialog } from '../../../../Common/Dialogs/LimitErrorDetailsDialog';
import PlatformInformation from '../../../../Common/PlatformInformation';
import MyAccountMenu from '../../../Common/MyAccountMenu';
import { TopUpGiftCard } from '../../Common/TopUpGiftCard';
import { GiftCardData, TopUpContextType } from '../../Common/types';
import { SuccessDialog } from '../SuccessDialog';

interface TopUpGiftCardState {
  mainImage?: string;
  isSuccessDialogVisible?: boolean;
  giftCardData: GiftCardData;
}

export const TopUpGiftCardPage = () => {
  const { customerBudget, setCustomerBudget } = useContext(
    CustomerBudgetContext,
  );

  const { t: translate } = useTranslation();
  const crumbs = applicationCrumbs(translate);
  const [state, setState] = useState<TopUpGiftCardState | null>(null);
  const [showErrorPanel, setShowErrorPanel] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorState>({});
  const [showLimitDialog, setShowLimitDialog] = useState<boolean>(false);
  const isSubmittingRef = useRef(false);
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const loadCardDetails = async (abortController?: AbortController) => {
    if (!params?.cardTypeId) return;
    const cardTypeId = Number.parseInt(params.cardTypeId);
    const resp = await cardService.getTopUpCardDetailsByCardType(
      cardTypeId,
      TopUpContextType.TopUp,
      {
        signal: abortController?.signal,
      },
    );

    if (resp.hasErrors) {
      if (!abortController?.signal.aborted) {
        setShowErrorPanel(true);
      }
      return;
    }

    const { cardProducts, topUpDetails, thumbnail, mainImage } = resp.data;

    const updateProductIdParam = (details?: TopUpDetailsModel) => {
      if (details) {
        // only to be displayed for the ops team, not actually used in code
        updateQueryStringParameter('productId', details.productId.toString());
      }
    };

    // --- CASE 1: single product
    if (cardProducts.length === 1) {
      updateProductIdParam(topUpDetails);

      setState({
        mainImage,
        giftCardData: {
          cardSeries: location.state?.cardSeries,
          thumbnail,
          showBeneficiaryTypeSelector: false,
          beneficiaryType: cardProducts[0].beneficiaryType,
          products: cardProducts,
          selectedProduct: cardProducts[0],
          topUpDetails,
        },
      });
      return;
    }

    // --- CASE 2: multiple products
    const employeeProducts = cardProducts.filter(
      e => e.beneficiaryType === GiftCardBeneficiaryType.Employee,
    );
    const dependentProducts = cardProducts.filter(
      e => e.beneficiaryType === GiftCardBeneficiaryType.Dependent,
    );
    const showBeneficiaryTypeSelector =
      !!employeeProducts.length && !!dependentProducts.length;

    let selectedProduct: CardProductModel | undefined;

    selectedProduct = resp.data.cardProducts.find(
      e => e.productId === topUpDetails?.productId,
    );

    updateProductIdParam(topUpDetails);

    let beneficiaryType;
    if (
      cardProducts.every(
        e => e.beneficiaryType === GiftCardBeneficiaryType.Dependent,
      )
    ) {
      beneficiaryType = GiftCardBeneficiaryType.Dependent;
    }
    if (
      cardProducts.every(
        e => e.beneficiaryType === GiftCardBeneficiaryType.Employee,
      )
    ) {
      beneficiaryType = GiftCardBeneficiaryType.Employee;
    }

    setState({
      giftCardData: {
        cardSeries: location.state?.cardSeries,
        thumbnail,
        showBeneficiaryTypeSelector,
        beneficiaryType,
        products: cardProducts,
        selectedProduct,
        topUpDetails,
      },
      mainImage: resp.data.mainImage,
    });
  };

  const saveData = async (
    productId: number,
    options: Dictionary<any>,
    serializedOptions: string,
    price: ComputedCardProductPriceModel,
  ) => {
    if (!state) return null;
    if (isSubmittingRef.current) return null;
    isSubmittingRef.current = true;
    setState(
      produce(draft => {
        draft!.giftCardData.isSaveButtonLoading = true;
      }),
    );
    let resp: HttpResponse<CheckoutResponseModel>;
    try {
      resp = await cardService.topUpCard({
        productId,
        options: serializedOptions,
      });
    } finally {
      isSubmittingRef.current = false;
      setState(
        produce(draft => {
          draft!.giftCardData.isSaveButtonLoading = false;
        }),
      );
    }
    if (!resp.hasErrors) {
      setState(
        produce(draft => {
          draft!.isSuccessDialogVisible = true;
        }),
      );
      setCustomerBudget(
        produce(customerBudget, d => {
          d.benefitBudgetBalance -= price.price;
        }),
      );
    } else {
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        showErrorToast(translate(resources.employeeCards.topUp.errorMessage));
      }
    }
  };

  const clearErrors = (optionId: number | string) => {
    setErrors(
      produce(draft => {
        if (draft) {
          draft[optionId] = [];
          draft.null = [];
        }
      }),
    );
  };

  useMount(abortController => {
    loadCardDetails(abortController);
  });

  if (!params?.cardTypeId) return null;

  return (
    <>
      <Breadcrumbs entries={[crumbs.home, crumbs.cards, crumbs.topUpCard]} />
      <section className="section-main section-actions my-account-page top-up-page">
        <div className="container">
          <div className="area-actions">
            <MyAccountMenu />
          </div>
          <div className="area-content">
            <PlatformInformation />
            <div className="panel padding-xl">
              <div className="btn-panel-back-wrapper">
                <a onClick={() => navigate(-1)} className="btn btn-panel-back">
                  <span className="btn-text">{translate(resources.cards)}</span>
                </a>
              </div>
              <h1>{translate(resources.employeeCards.topUp.title)}</h1>
              {hasValidationErrors(errors, 'null') && (
                <Notification
                  type={NotificationType.Error}
                  messages={errors.null.map(e => ({
                    message: getErrorMessage(e, translate),
                    onShowDetailsClick: e.payload?.isAutobudgetLimitError
                      ? () => setShowLimitDialog(true)
                      : undefined,
                  }))}
                />
              )}
              <Loader
                loading={!state}
                loader={<LoadingSpinner />}
                showErrorPanel={showErrorPanel}
              >
                {!!state && (
                  <>
                    <TopUpGiftCard
                      contextType={TopUpContextType.TopUp}
                      giftCardData={state.giftCardData}
                      setGiftCardData={newData =>
                        setState(
                          produce(d => {
                            d!.giftCardData = newData;
                          }),
                        )
                      }
                      onSaveClick={saveData}
                      errors={errors}
                      setErrors={setErrors}
                      clearErrors={clearErrors}
                    />
                    {state.isSuccessDialogVisible && (
                      <SuccessDialog mainImage={state.mainImage} />
                    )}
                  </>
                )}
              </Loader>
            </div>
          </div>
        </div>
      </section>
      {showLimitDialog && (
        <LimitErrorDetailsDialog
          closeDialog={() => setShowLimitDialog(false)}
        />
      )}
    </>
  );
};
